import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@shared/ui/dialog';
import { Button } from '@shared/ui';
import { useVerifyEmail } from '@features/auth/verify-email';
import { LocaleThemeControls } from '@features/change-language';

interface VerifyEmailModalProps {
  open: boolean;
  onClose: () => void;
  email?: string;
  /** Registration already sends verification email on the backend. */
  skipInitialSend?: boolean;
}

export function VerifyEmailModal({
  open,
  onClose,
  email,
  skipInitialSend = false,
}: VerifyEmailModalProps) {
  const { t } = useTranslation('auth');
  const { sendEmail, isLoading } = useVerifyEmail();
  const [countdown, setCountdown] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const hasSentOnOpen = useRef(false);

  useEffect(() => {
    if (!open) {
      hasSentOnOpen.current = false;
      setCountdown(60);
      setCanResend(false);
      return;
    }

    if (countdown > 0) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [open, countdown]);

  const handleInitialSend = useCallback(async () => {
    if (hasSentOnOpen.current) {
      return;
    }

    hasSentOnOpen.current = true;
    await sendEmail();
  }, [sendEmail]);

  useEffect(() => {
    if (open && !skipInitialSend) {
      handleInitialSend();
    }
  }, [open, skipInitialSend, handleInitialSend]);

  const handleResend = async () => {
    if (!canResend || isLoading) {
      return;
    }

    const success = await sendEmail();
    if (success) {
      setCountdown(60);
      setCanResend(false);
    }
  };

  const maskedEmail = email
    ? email.replace(/(.{2})(.*)(@.*)/, '$1***$3')
    : '';

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <div className="absolute right-[3.75rem] top-4 z-10 flex items-center sm:right-[3.25rem]">
          <LocaleThemeControls
            iconClassName="h-4 w-4"
            triggerClassName="size-tap rounded-sm text-foreground opacity-70 hover:bg-transparent hover:text-foreground hover:opacity-100 sm:size-8"
          />
        </div>

        <DialogHeader>
          <DialogTitle>{t('confirmEmail')}</DialogTitle>
          <DialogDescription>
            {maskedEmail
              ? t('emailSentToAddress', { email: maskedEmail })
              : t('emailConfirmInstructions')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <p className="text-sm text-muted-foreground">{t('emailConfirmInstructions')}</p>
          <p className="text-sm text-muted-foreground">{t('noCodeYet')}</p>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" className="w-full sm:w-auto" onClick={onClose}>
            {t('back')}
          </Button>
          <Button
            type="button"
            variant="default"
            className="w-full sm:w-auto"
            onClick={handleResend}
            loading={isLoading}
            disabled={!canResend}
          >
            {canResend ? t('resendEmail') : t('requestCodeIn', { seconds: countdown })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
